import * as turf from "@turf/turf";
import type { Feature, FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import type { FloorInfo, RoutingGraph, RoutingGraphEdge, RoutingGraphNode } from "../types/routing";
import { FaicilityCoordsForPathFind } from "../types/routingInterface";
import { calculateMapConfig, transformGeoJSONCoords } from "@/utils/mapUtils";

/**
 * 経路のgeojsonデータをスケーリングしてからjsonとして返す
 * @param floorId
 * @returns
 */
const manualGraphCache = new Map<string, FeatureCollection>();

export const loadManualGraphData = async (floorId: string): Promise<FeatureCollection> => {
  try {
    const cached = manualGraphCache.get(floorId);
    if (cached) {
      return cached;
    }

    const graphDataFilePath = floorId + "-path.geojson";

    const graphData: FeatureCollection = await fetch(graphDataFilePath).then((res) => {
      if (!res.ok) {
        throw new Error(`Failed to load Graph File: ${res.status}`);
      }
      return res.json();
    });

    const { scaleFactor } = calculateMapConfig();
    const scaledGraphData = transformGeoJSONCoords(graphData, scaleFactor);

    manualGraphCache.set(floorId, scaledGraphData);
    return scaledGraphData;
  } catch (error) {
    console.error("Graphデータの読み込み・変換に失敗:", error);
    throw error;
  }
};

/**
 * x,yから最も近い経路上にノードを追加する
 * グラフ変更あり
 * @param graph
 * @param x
 * @param y
 * @returns
 */
export const createNearestNodeOnLine = async (
  graph: RoutingGraph,
  x: number,
  y: number,
): Promise<RoutingGraphNode | null> => {
  let nearestLine: number[][] | null = null;
  let nearestPoint: number[] | null = null;
  let isNearestPointEndPoint: boolean = false;
  let minDistance = Infinity;

  const point = { x: x, y: y };

  const lineGeoJson = await loadManualGraphData(graph.floorId);
  const lineArrays = lineGeoJson.features.map((oneLineGeoJson) => {
    return JSON.parse(JSON.stringify(oneLineGeoJson.geometry)).coordinates as number[][];
  });

  for (const line of lineArrays) {
    const p1 = { x: line[0][0], y: line[0][1] };
    const p2 = { x: line[1][0], y: line[1][1] };
    const _line = { p1: p1, p2: p2 };
    const [distance, _nearestPoint, isEndPoint] = getDistanceFromPointToLine(point, _line);

    if (distance < minDistance) {
      minDistance = distance;
      nearestLine = line;
      nearestPoint = [_nearestPoint.x, _nearestPoint.y];
      isNearestPointEndPoint = isEndPoint;
    }
  }

  if (!nearestLine || !nearestPoint) return null;

  const floorId = graph.floorId;

  const nearestNode: RoutingGraphNode = generateNode(nearestPoint[0], nearestPoint[1], floorId);
  if (isNearestPointEndPoint) {
    //これはなくてもうまくいくかもしれない（同じノード、同じノード間のエッジは追加されないようになっているので）
    return nearestNode;
  }
  addNodeToNodeMap(nearestNode.id, nearestNode, graph.nodes);

  const p1_node = generateNode(nearestLine[0][0], nearestLine[0][1], floorId);
  const p2_node = generateNode(nearestLine[1][0], nearestLine[1][1], floorId);

  //新しいエッジが追加される前にもともとあったエッジを削除する
  //この処理はなくても問題なく動くかもしれないが一応
  const edges_removed: RoutingGraphEdge[] = generateTwoEdgesFromTwoNodes(p1_node, p2_node);
  for (const edge_removed of edges_removed) {
    const edgeArray = graph.edges.get(edge_removed.from);
    if (edgeArray === undefined) {
      continue;
    }

    //削除するエッジのインデックスを取得
    const indexToRemove = edgeArray.findIndex((edge) => {
      return edge.id === edge_removed.id;
    });
    if (indexToRemove !== -1) {
      //インデックスの位置から1個削除
      edgeArray.splice(indexToRemove, 1);
    }
  }

  const edge1 = generateTwoEdgesFromTwoNodes(p1_node, nearestNode);
  const edge2 = generateTwoEdgesFromTwoNodes(p2_node, nearestNode);
  const edges_added: RoutingGraphEdge[] = [...edge1, ...edge2];

  for (const edge_added of edges_added) {
    addEdgeToEdgeMap(edge_added.from, edge_added, graph.edges);
  }

  return nearestNode;
};

/**
 * createNearestNodeOnLineの後に使うやつ
 * 現在までのnearestNodeへとつながるドアノードをグラフに追加
 * グラフ変更あり
 * @param graph
 * @param tempNearestNode
 * @param facilityCoords
 * @returns
 */
export const updateNearestNodeToDoorNode = (
  graph: RoutingGraph,
  tempNearestNode: RoutingGraphNode,
  facilityCoords: FaicilityCoordsForPathFind,
): RoutingGraphNode => {
  const doorNode: RoutingGraphNode = generateNode(
    facilityCoords.position.x,
    facilityCoords.position.y,
    facilityCoords.floorId,
  );
  addNodeToNodeMap(doorNode.id, doorNode, graph.nodes);

  const doorToTempStartEdges = generateTwoEdgesFromTwoNodes(tempNearestNode, doorNode);
  for (const doorToTempStartEdge of doorToTempStartEdges) {
    addEdgeToEdgeMap(doorToTempStartEdge.from, doorToTempStartEdge, graph.edges);
  }

  return doorNode; //仮にtempNearestNodeとdoorNodeが同じ場合には同じノードが返される
};

/**
 * ノード間のエッジを作成
 * @param node1
 * @param node2
 * @param cost
 * @param linkType
 * @returns
 */
export const generateTwoEdgesFromTwoNodes = (
  node1: RoutingGraphNode,
  node2: RoutingGraphNode,
  cost?: number,
  linkType?: "stairs" | "elevator",
): [edge_node1Tonode2: RoutingGraphEdge, edge_node2Tonode1: RoutingGraphEdge] => {
  //２つのノードペアで双方向へのエッジとして２つエッジ作成
  const edge_forward = generateEdge(
    { id: node1.id, x: node1.position.x, y: node1.position.y },
    { id: node2.id, x: node2.position.x, y: node2.position.y },
    cost,
    linkType,
  );
  const edge_back = generateEdge(
    { id: node2.id, x: node2.position.x, y: node2.position.y },
    { id: node1.id, x: node1.position.x, y: node1.position.y },
    cost,
    linkType,
  );

  return [edge_forward, edge_back];
};

/**
 * geojsonのlinestringの両端点のノード作成
 * @param lineFeature
 * @returns
 */
export const generateNodeFromLineFeature = (
  lineFeature: Feature<Geometry, GeoJsonProperties>,
): [node1: RoutingGraphNode, node2: RoutingGraphNode] | null => {
  const properties = lineFeature.properties;
  const coordinates: number[][] = JSON.parse(JSON.stringify(lineFeature.geometry)).coordinates; //この型エラーどうにかならないかな

  if (!properties) return null;

  const p1 = { x: coordinates[0][0], y: coordinates[0][1] };
  const p2 = { x: coordinates[1][0], y: coordinates[1][1] };
  const floorId = properties.floorId;

  //lineStringの両端の点をノードとする
  const p1_node = generateNode(p1.x, p1.y, floorId);
  const p2_node = generateNode(p2.x, p2.y, floorId);

  return [p1_node, p2_node];
};

/**
 * グラフにノード追加するときはこの関数を使用
 * グラフ変更あり
 * @param nodeId
 * @param node
 * @param nodes
 */
export const addNodeToNodeMap = (
  nodeId: string,
  node: RoutingGraphNode,
  nodes: Map<string, RoutingGraphNode>,
): void => {
  if (!nodes.has(nodeId)) {
    nodes.set(nodeId, node);
  }
};

/**
 * グラフにエッジを追加するときはこの関数を使用
 * グラフ変更あり
 * @param fromNodeId
 * @param edge
 * @param edges
 * @returns
 */
export const addEdgeToEdgeMap = (
  fromNodeId: string,
  edge: RoutingGraphEdge,
  edges: Map<string, RoutingGraphEdge[]>,
): void => {
  //同じノード間のエッジは追加しない
  if (edge.from === edge.to) {
    return;
  }

  if (!edges.has(fromNodeId)) {
    edges.set(fromNodeId, []);
  }

  //すでに同じエッジがある場合には追加しない
  if (edges.get(fromNodeId)!.some((now_edge) => now_edge.id === edge.id)) return;
  edges.get(fromNodeId)!.push(edge);
};

/**
 * エッジ作成用関数
 * @param fromNodeInfo
 * @param toNodeInfo
 * @param _cost
 * @param linkType
 * @returns
 */
export const generateEdge = (
  fromNodeInfo: { id: string; x: number; y: number },
  toNodeInfo: { id: string; x: number; y: number },
  _cost?: number,
  linkType?: "stairs" | "elevator",
): RoutingGraphEdge => {
  let cost: number;
  if (_cost === undefined) {
    //指定がなければノード間の座標距離をコストとする
    cost = dist({ x: fromNodeInfo.x, y: fromNodeInfo.y }, { x: toNodeInfo.x, y: toNodeInfo.y });
  } else {
    cost = _cost;
  }
  const edge: RoutingGraphEdge = {
    id: generateGraphEdgeID(fromNodeInfo.id, toNodeInfo.id),
    from: fromNodeInfo.id,
    to: toNodeInfo.id,
    cost: cost,
    linkType: linkType,
  };
  return edge;
};

/**
 * ノード作成用関数
 * @param x
 * @param y
 * @param floorId
 * @returns
 */
export const generateNode = (x: number, y: number, floorId: string): RoutingGraphNode => {
  const node: RoutingGraphNode = {
    id: generateGraphNodeID(x, y, floorId),
    position: {
      x: x,
      y: y,
    },
    floorId: floorId,
    floor: parseInt(floorId.split("-")[1]),
  };
  return node;
};

/**
 * エッジのIDを生成する関数
 * @param fromNode
 * @param toNode
 * @returns
 */
export const generateGraphEdgeID = (fromNode: string, toNode: string) => {
  return `${fromNode}-${toNode}`;
};

/**
 * ノードのIDを生成する関数
 * @param x
 * @param y
 * @param floorId
 * @returns
 */
export const generateGraphNodeID = (x: number, y: number, floorId: string) => {
  return `${floorId}-${x}-${y}`;
};

// ユーティリティ関数: 2点間の距離の2乗
const distSq = (v: { x: number; y: number }, w: { x: number; y: number }) => {
  return Math.pow(v.x - w.x, 2) + Math.pow(v.y - w.y, 2);
};

// ユーティリティ関数: 2点間の距離
export const dist = (v: { x: number; y: number }, w: { x: number; y: number }) => {
  return Math.sqrt(distSq(v, w));
};

/**
 * 点と線分の距離を計算
 * isEndPointは一番近い線上の点が端点かどうか
 * @param point
 * @param line
 * @returns
 */
export const getDistanceFromPointToLine = (
  point: { x: number; y: number },
  line: { p1: { x: number; y: number }; p2: { x: number; y: number } },
): [distance: number, nearestPoint: { x: number; y: number }, isEndPoint: boolean] => {
  let isEndPoint = false;

  const p = point;
  const a = line.p1;
  const b = line.p2;

  // --- メインロジック ---

  // 1. 線分ABの長さの2乗を計算 ( |b - a|^2 )
  const lineLengthSq = distSq(a, b);

  // 2. 線分の長さが0の場合 (aとbが同じ点)
  if (lineLengthSq === 0) {
    isEndPoint = true;
    return [
      dist(p, a), // 点Pと点Aの距離を返す,
      a,
      isEndPoint,
    ];
  }

  if (p.x === a.x && p.y === a.y) {
    isEndPoint = true;
    return [0, a, isEndPoint];
  }
  if (p.x === b.x && p.y === b.y) {
    isEndPoint = true;
    return [0, b, isEndPoint];
  }

  // 3. 点Pの線分ABへの射影を計算
  //    ベクトルAP (p - a) と ベクトルAB (b - a) の内積を求める
  const ap_x = p.x - a.x;
  const ap_y = p.y - a.y;
  const ab_x = b.x - a.x;
  const ab_y = b.y - a.y;

  const dotProduct = ap_x * ab_x + ap_y * ab_y;

  // 射影の位置を示すパラメータtを計算 ( 0 <= t <= 1 なら線分上 )
  // t = (AP · AB) / |AB|^2
  const t = dotProduct / lineLengthSq;

  let closestPoint: { x: number; y: number };

  // 4. tの値によって、最短距離の計算対象 (closestPoint) を決定
  if (t < 0) {
    // 射影がAの手前にある -> 最も近いのは端点A
    closestPoint = a;
    isEndPoint = true;
  } else if (t > 1) {
    // 射影がBの先にある -> 最も近いのは端点B
    closestPoint = b;
    isEndPoint = true;
  } else {
    // 射影が線分AB上にある -> 最も近いのは垂線の足
    closestPoint = {
      x: a.x + t * ab_x,
      y: a.y + t * ab_y,
    };
    isEndPoint = false;
  }

  // 5. 点Pと、線分上で最も近い点 (closestPoint) との距離を返す
  return [dist(p, closestPoint), closestPoint, isEndPoint];
};

const findDoorFromFacilityId = (
  facilityId: string,
  icons: FeatureCollection,
): { x: number; y: number } | null => {
  const doorIconForFaiclity = icons.features.find((feature) => {
    if (!feature.properties) return false;
    return feature.properties.pair_facilityId === facilityId;
  });

  if (doorIconForFaiclity === undefined) {
    return null;
  }

  const doorCoordinates: number[] = JSON.parse(
    JSON.stringify(doorIconForFaiclity.geometry),
  ).coordinates;
  return {
    x: doorCoordinates[0],
    y: doorCoordinates[1],
  };
};

/**
 * 施設に対応する座標を取得
 * ドアがある場合には対応するドア座標、ない場合には施設の重心位置を返す
 */
export async function findCoordsWithFloorForFacility(
  facilityId: string,
  scaledPolygonsMap: Map<string, FeatureCollection>,
  iconsMap: Map<string, FeatureCollection>,
  graphs: Map<string, RoutingGraph>,
): Promise<FaicilityCoordsForPathFind | null> {
  // 1. 施設IDから階層を特定
  const floorInfo = extractFloorInfo(facilityId);
  if (!floorInfo) return null;

  const { floorId } = floorInfo;
  const scaledPolygons = scaledPolygonsMap.get(floorId);
  const icons = iconsMap.get(floorId);
  const graph = graphs.get(floorId);

  if (!scaledPolygons || !icons || !graph) return null;

  const doorPosition = findDoorFromFacilityId(facilityId, icons);
  if (doorPosition) {
    return {
      position: {
        x: doorPosition.x,
        y: doorPosition.y,
      },
      floorId: floorId,
      isDoor: true,
    };
  }

  // 施設ポリゴンを取得
  const facilityFeature = scaledPolygons.features.find((f) => f.properties?.id === facilityId);

  if (!facilityFeature) return null;

  // 施設の重心を計算
  const center = turf.centroid(facilityFeature);
  const [centroidX, centroidY] = center.geometry.coordinates;
  if (centroidX === undefined || centroidY === undefined) {
    console.warn("施設IDから施設座標を取得するさいに施設の重心を計算するのに失敗しました。");
    return null;
  }

  //対応するドアがなかった場合には重心座標をreturn
  return {
    position: {
      x: centroidX,
      y: centroidY,
    },
    floorId: floorId,
    isDoor: false,
  };
}

/**
 * 施設IDから階層情報を抽出
 * 例: "M-3-classroom-363" → { floorId: "main-3", floor: 3, location: "M" }
 */
export const extractFloorInfo = (facilityId: string): FloorInfo | null => {
  const parts = facilityId.split("-");
  if (parts.length < 2) return null;

  const location = parts[0]; // "M" or "R"
  const floor = parseInt(parts[1] ?? "3");

  if (isNaN(floor)) return null;

  const floorId =
    location === "M" ? `main-${floor}` : location === "R" ? `research-${floor}` : null;

  if (!floorId) return null;

  return { floorId, floor };
};

/**
 * グラフマップ内でノードを検索
 */
export function findNodeInGraphs(
  graphs: Map<string, RoutingGraph>,
  nodeId: string,
): RoutingGraphNode | null {
  for (const graph of graphs.values()) {
    if (graph.nodes.has(nodeId)) {
      return graph.nodes.get(nodeId)!;
    }
  }
  return null;
}

/**
 * グラフマップ内でノードのエッジを取得
 */
export function findEdgesForNode(
  graphs: Map<string, RoutingGraph>,
  nodeId: string,
): RoutingGraphEdge[] {
  for (const graph of graphs.values()) {
    if (graph.edges.has(nodeId)) {
      return graph.edges.get(nodeId)!;
    }
  }
  return [];
}

/**
 * 経路ノードからGeoJSON LineStringを生成
 */
export function createRouteLineGeometry(path: RoutingGraphNode[]): FeatureCollection {
  // 階層ごとに経路を分割し、フロア変更の境界を正しく扱う
  const lineStrings: Feature[] = [];

  let currentFloorNodes: RoutingGraphNode[] = [];
  let currentFloorId = path[0]?.floorId;

  for (let i = 0; i < path.length; i++) {
    const node = path[i];
    if (node === undefined) continue;

    // 階層が変わった場合
    if (node.floorId !== currentFloorId) {
      // 現在の階層の経路を保存（最低2点必要）
      if (currentFloorNodes.length >= 2) {
        const coordinates = currentFloorNodes.map((n) => [n.position.x, n.position.y]);

        lineStrings.push({
          type: "Feature",
          properties: { floorId: currentFloorId },
          geometry: {
            type: "LineString",
            coordinates,
          },
        });
      }

      // 新しい階層を開始
      currentFloorId = node.floorId;
      currentFloorNodes = [node];
    } else {
      // 同じ階層内のノードを追加
      currentFloorNodes.push(node);
    }
  }

  // 最後の階層の経路を保存
  if (currentFloorNodes.length >= 2) {
    const coordinates = currentFloorNodes.map((n) => [n.position.x, n.position.y]);

    lineStrings.push({
      type: "Feature",
      properties: { floorId: currentFloorId },
      geometry: {
        type: "LineString",
        coordinates,
      },
    });
  }

  return {
    type: "FeatureCollection",
    features: lineStrings,
  };
}
